import { useEffect, useRef, useState } from "react";
import { MapPin, Navigation2, Loader2, Crosshair, Plane, Home, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

/**
 * Internal Driver GPS Map Widget
 * - Shows driver's current location on OpenStreetMap (no API key needed, no Google fees).
 * - Highlights the next pickup + drop-off pins from the active bookings.
 * - Auto-tracks via watchPosition while "Live Tracking" is on, posting back to backend every 30s.
 * - Provides one-tap "Open in Waze" / "Open in Google Maps" for navigation handoff.
 */
export default function DriverGpsMap({ bookings = [] }) {
  const [pos, setPos] = useState(null); // {lat, lng, accuracy}
  const [tracking, setTracking] = useState(false);
  const [error, setError] = useState(null);
  const watchIdRef = useRef(null);
  const lastPushRef = useRef(0);
  const iframeRef = useRef(null);

  const driverToken = () => localStorage.getItem("driver_token");

  const pushLocation = async (lat, lng) => {
    try {
      await axios.post(
        `${API}/driver/update-location?latitude=${lat}&longitude=${lng}`,
        {},
        { headers: { Authorization: `Bearer ${driverToken()}` } }
      );
    } catch (_e) { /* silent — non-fatal */ }
  };

  const stopTracking = () => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    watchIdRef.current = null;
    setTracking(false);
  };

  const startTracking = () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported on this device");
      toast.error("Geolocation not supported");
      return;
    }
    setError(null);
    setTracking(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (p) => {
        const lat = p.coords.latitude;
        const lng = p.coords.longitude;
        setPos({ lat, lng, accuracy: p.coords.accuracy });
        // Push to backend at most once every 30s to limit load
        const now = Date.now();
        if (now - lastPushRef.current > 30_000) {
          lastPushRef.current = now;
          pushLocation(lat, lng);
        }
      },
      (e) => {
        setError(e.message || "Failed to get location");
        setTracking(false);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
  };

  useEffect(() => () => stopTracking(), []);

  // Build OpenStreetMap embed URL centered on the driver
  const mapSrc = pos
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${pos.lng - 0.05}%2C${pos.lat - 0.03}%2C${pos.lng + 0.05}%2C${pos.lat + 0.03}&layer=mapnik&marker=${pos.lat}%2C${pos.lng}`
    : null;

  // Pick the next active booking (first non-completed, non-cancelled in passed list)
  const nextBooking = bookings.find(b => b.booking_status !== "completed" && b.booking_status !== "cancelled");

  const openInGoogle = (target) => {
    const q = encodeURIComponent(target);
    const url = pos
      ? `https://www.google.com/maps/dir/${pos.lat},${pos.lng}/${q}`
      : `https://www.google.com/maps/search/?api=1&query=${q}`;
    window.open(url, "_blank");
  };

  const openInWaze = (target) => {
    const q = encodeURIComponent(target);
    window.open(`https://waze.com/ul?q=${q}&navigate=yes`, "_blank");
  };

  return (
    <Card className="border-0 shadow-lg rounded-2xl overflow-hidden" data-testid="driver-gps-card">
      <CardContent className="p-3 md:p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${tracking ? "bg-green-500" : "bg-gray-300"}`}>
              <Navigation2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-base flex items-center gap-2">
                Internal GPS
                {tracking && <Badge className="bg-green-500 text-white text-[10px] animate-pulse">LIVE</Badge>}
              </h3>
              <p className="text-xs text-muted-foreground">
                {pos
                  ? `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)} (±${Math.round(pos.accuracy)}m)`
                  : "Location not active"}
              </p>
            </div>
          </div>
          {!tracking ? (
            <Button onClick={startTracking} size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full" data-testid="gps-start-btn">
              <Crosshair className="w-4 h-4 mr-1" /> Start
            </Button>
          ) : (
            <Button onClick={stopTracking} size="sm" variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="gps-stop-btn">
              <Power className="w-4 h-4 mr-1" /> Stop
            </Button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-2 text-xs text-red-800 mb-3">
            {error}
          </div>
        )}

        {/* Embedded Map */}
        <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50" style={{ height: "260px" }}>
          {mapSrc ? (
            <iframe
              ref={iframeRef}
              src={mapSrc}
              title="driver-map"
              width="100%"
              height="260"
              className="border-0"
              data-testid="gps-iframe-map"
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
              {tracking ? <Loader2 className="w-6 h-6 animate-spin" /> : <MapPin className="w-8 h-8" />}
              <p className="text-sm">{tracking ? "Acquiring location..." : "Tap Start to enable GPS"}</p>
            </div>
          )}
        </div>

        {/* Next Booking — Quick Navigation */}
        {nextBooking && (
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="gps-next-booking">
            <p className="text-xs text-blue-900 font-semibold mb-2">Next Booking — Quick Nav</p>
            <div className="space-y-2">
              {nextBooking.pickup_location && (
                <div className="flex items-center gap-2 text-xs">
                  <Plane className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  <span className="flex-1 truncate font-medium">{nextBooking.pickup_location}</span>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => openInWaze(nextBooking.pickup_location)} data-testid="gps-waze-pickup">Waze</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => openInGoogle(nextBooking.pickup_location)} data-testid="gps-google-pickup">Google</Button>
                </div>
              )}
              {(nextBooking.dropoff_location || nextBooking.housing_address || nextBooking.housing_unit) && (
                <div className="flex items-center gap-2 text-xs">
                  <Home className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                  <span className="flex-1 truncate font-medium">{nextBooking.dropoff_location || nextBooking.housing_address || nextBooking.housing_unit}</span>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => openInWaze(nextBooking.dropoff_location || nextBooking.housing_address || nextBooking.housing_unit)} data-testid="gps-waze-dropoff">Waze</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => openInGoogle(nextBooking.dropoff_location || nextBooking.housing_address || nextBooking.housing_unit)} data-testid="gps-google-dropoff">Google</Button>
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground mt-3 leading-tight">
          GPS coordinates auto-shared with admin every 30 sec while tracking is on. Map data: © OpenStreetMap contributors.
        </p>
      </CardContent>
    </Card>
  );
}
